/**
 * Copyright (c) 2024, CYBRA Corp. All rights reserved.
 */
import { Component, ElementRef, ViewChild } from "@angular/core";
import { SimpleIndefiniteProgressDialogComponent } from "app/components/simple-indefinite-progress-dialog/simple-indefinite-progress-dialog.component";
import { BuchananService } from "app/services/buchanan.service";

@Component({
  templateUrl: './buchanan.product-upload.component.html',
  styleUrls: ['./buchanan.product-upload.component.css']
})
export class BuchananProductUploadComponent {

  /** Progress Dialog displayed when performing actions to give the User an indication something is happening... */
  @ViewChild(SimpleIndefiniteProgressDialogComponent) indefiniteProgressDialog: SimpleIndefiniteProgressDialogComponent;

  /** File Upload Input Element being used for the CSV Data File being uploaded */
  @ViewChild('csvFile')
  csvFileInput: ElementRef;

  /** Holds the currently selected CSV File to be uploaded */
  selectedFile: File = null;

  /** Holds whether we are currently uploading a File or not */
  isUploading: boolean = false;

  /** Message to be displayed to the User.  This contains the result of the upload */
  userMessage: string = null;

  /** Boolean flag indicating if the message being displayed to the User is an error message or not (success in that case) */
  isErrorMessage: boolean = false;


  /** Constructor */
  constructor(
    private buchananService: BuchananService
  ) { }


  /** 
   * Called when the "Select File" UI Button is clicked.  Displays the "File Prompt" so that
   * the User can select the File to upload. 
   */
  selectFileClicked(): void {
    this.csvFileInput.nativeElement.click();

    // Reset the message variables to hide the message if one is being displayed
    this.userMessage = null;
    this.isErrorMessage = false;
  }

  /**
   * Called when the User selects the File to be uploaded.  Stores the selected File to be uploaded 
   * when the User clicks the "Upload" UI Button.  
   */
  onSelectedFileChanged(event: any): void {
    let newSelectedFile: File = null;
    if (event.target.files.length > 0) {
      newSelectedFile = event.target.files[0];
    }

    this.selectedFile = newSelectedFile;
  }
  
  /**
   * Called when the User clicks the "Upload" UI Button.  Displays the Progress Dialog and starts
   * the Upload process in the background. 
   */
  uploadClicked(): void {
    if (!this.isUploading) {
      this.isUploading = true;
      this.indefiniteProgressDialog.show("Uploading", "Uploading File...");
      this._uploadFile();
    }
  }

  /**
   * Upload Process.  Calls the Upload API and handles the message to display to the User
   * on Success / Error. This also hides the Progress Dialog before finishing. 
   */
  private async _uploadFile(): Promise<void> {
    try {
      await this.buchananService.uploadProductFile(this.selectedFile);

      this.userMessage = "Successfully uploaded File!";
      this.isErrorMessage = false;
    } catch (error) {
      let message = "Problem uploading File.";
      if (error?.error != null) {
        message = message + `<br/>Error: ${error.error}`;
      }
      this.userMessage = message;
      this.isErrorMessage = true;
    } finally {
      this.indefiniteProgressDialog.hide();
      this.isUploading = false;
      this.selectedFile = null;
    }
  }

}
